import { forwardRef, useEffect, ForwardRefExoticComponent, RefAttributes, FC } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { Chip, ListItemButton, ListItemIcon, ListItemText, Typography, useMediaQuery } from '@mui/material';
import { Avatar } from '../../../../../components/Avatar';
import Dot from '../../../../../components/@extended/Dot';
import useConfig from '../../../../../hooks/useConfig';
import { useMenu } from '../../../../../store/menu';
import { LinkTarget, NavItemType } from '../../../../../types/menu';
import { MenuOrientation, ThemeMode } from '../../../../../types/config';

interface Props {
	item: NavItemType;
	level: number;
}

const NavItem: FC<Props> = ({ item, level }) => {
	const theme = useTheme();
	const location = useLocation();

	const { state: menu, activeItem, openDrawer } = useMenu();
	const matchDownLg = useMediaQuery(theme.breakpoints.down('lg'));
	const { drawerOpen } = menu;

	const downLG = useMediaQuery(theme.breakpoints.down('lg'));

	const { menuOrientation } = useConfig();
	let itemTarget: LinkTarget = '_self';
	if (item.target) {
		itemTarget = '_blank';
	}

	let listItemProps: {
		component: ForwardRefExoticComponent<RefAttributes<HTMLAnchorElement>> | string;
		href?: string;
		target?: LinkTarget;
	} = { component: forwardRef((props, ref) => <Link {...props} to={item.url!} target={itemTarget} ref={ref} />) };
	if (item.external) {
		listItemProps = { component: 'a', href: item.url, target: itemTarget };
	}

	const Icon = item.icon!;
	const itemIcon = item.icon ? <Icon style={{ fontSize: drawerOpen ? '1.5rem' : '1.25rem' }} /> : false;

	const isSelected = item.isActive ? item.isActive(location) : false;

	const { pathname } = location;

	// active menu item on page load
	useEffect(() => {
		if (pathname && pathname.includes('product-details')) {
			if (item.url && item.url.includes('product-details')) {
				activeItem([item.id ?? '']);
			}
		}

		if (pathname && pathname.includes('kanban')) {
			if (item.url && item.url.includes('kanban')) {
				activeItem([item.id ?? '']);
			}
		}

		if (pathname === item.url) {
			activeItem([item.id ?? '']);
		}
		// eslint-disable-next-line
	}, [pathname]);

	const textColor = theme.palette.mode === ThemeMode.DARK ? 'grey.400' : 'text.primary';
	const iconSelectedColor = theme.palette.mode === ThemeMode.DARK && drawerOpen ? 'text.primary' : 'grey.700';

	return (
		<>
			{menuOrientation === MenuOrientation.VERTICAL || downLG ? (
				<ListItemButton
					{...listItemProps}
					disabled={item.disabled}
					selected={isSelected}
					sx={{
						zIndex: 1201,
						pl: drawerOpen ? `${level * 28}px` : 1.5,
						py: !drawerOpen && level === 1 ? 1.25 : 1,
						...(drawerOpen && {
							'&:hover': {
								bgcolor: theme.palette.mode === ThemeMode.DARK ? 'divider' : 'primary.lighter',
							},
							'&.Mui-selected': {
								background:
									'linear-gradient(262.05deg, rgba(255, 204, 22, 0.3) 7.52%, rgba(163, 95, 14, 0.192188) 41.38%, rgba(255, 255, 255, 0) 96.1%)',
								borderRight: `2px solid ${theme.palette.primary.main}`,
								color: iconSelectedColor,
								'&:hover': {
									color: iconSelectedColor,
									bgcolor: theme.palette.mode === ThemeMode.DARK ? 'divider' : 'primary.lighter',
								},
							},
						}),
						...(!drawerOpen && {
							'&:hover': {
								bgcolor: 'transparent',
							},
							'&.Mui-selected': {
								'&:hover': {
									bgcolor: 'transparent',
								},
								bgcolor: 'transparent',
							},
						}),
					}}
					{...(matchDownLg && {
						onClick: () => openDrawer(false),
					})}
				>
					{itemIcon && (
						<ListItemIcon
							sx={{
								marginRight: '20px',
								minWidth: 28,
								color: isSelected ? iconSelectedColor : '#8C8C8C',
								...(!drawerOpen && {
									borderRadius: 1.5,
									width: 36,
									height: 36,
									alignItems: 'center',
									justifyContent: 'center',
									'&:hover': {
										bgcolor: theme.palette.mode === ThemeMode.DARK ? 'secondary.light' : 'secondary.lighter',
									},
								}),
								...(!drawerOpen &&
									isSelected && {
										bgcolor: theme.palette.mode === ThemeMode.DARK ? 'primary.900' : 'primary.lighter',
										'&:hover': {
											bgcolor: theme.palette.mode === ThemeMode.DARK ? 'primary.darker' : 'primary.lighter',
										},
									}),
							}}
						>
							{itemIcon}
						</ListItemIcon>
					)}
					{(drawerOpen || level !== 1) && (
						<ListItemText
							primary={
								<Typography variant={'h6'} sx={{ color: isSelected ? iconSelectedColor : textColor }}>
									{item.title}
								</Typography>
							}
						/>
					)}
					{(drawerOpen || level !== 1) && item.chip && (
						<Chip
							color={item.chip.color}
							variant={item.chip.variant}
							size={item.chip.size}
							label={item.chip.label}
							avatar={item.chip.avatar && <Avatar>{item.chip.avatar}</Avatar>}
						/>
					)}
				</ListItemButton>
			) : (
				<ListItemButton
					{...listItemProps}
					disabled={item.disabled}
					selected={isSelected}
					sx={{
						zIndex: 1201,
						...(drawerOpen && {
							'&:hover': {
								bgcolor: 'transparent',
							},
							'&.Mui-selected': {
								bgcolor: 'transparent',
								color: iconSelectedColor,
								'&:hover': {
									color: iconSelectedColor,
									bgcolor: 'transparent',
								},
							},
						}),
						...(!drawerOpen && {
							'&:hover': {
								bgcolor: 'transparent',
							},
							'&.Mui-selected': {
								'&:hover': {
									bgcolor: 'transparent',
								},
								bgcolor: 'transparent',
							},
						}),
					}}
				>
					{itemIcon && (
						<ListItemIcon
							sx={{
								minWidth: 36,
								...(!drawerOpen && {
									borderRadius: 1.5,
									width: 36,
									height: 36,
									alignItems: 'center',
									justifyContent: 'flex-start',
									'&:hover': {
										bgcolor: 'transparent',
									},
								}),
								...(!drawerOpen &&
									isSelected && {
										bgcolor: 'transparent',
										'&:hover': {
											bgcolor: 'transparent',
										},
									}),
							}}
						>
							{itemIcon}
						</ListItemIcon>
					)}

					{!itemIcon && (
						<ListItemIcon
							sx={{
								color: isSelected ? 'primary.main' : 'secondary.main',
								...(!drawerOpen && {
									borderRadius: 1.5,
									alignItems: 'center',
									justifyContent: 'flex-start',
									'&:hover': {
										bgcolor: 'transparent',
									},
								}),
								...(!drawerOpen &&
									isSelected && {
										bgcolor: 'transparent',
										'&:hover': {
											bgcolor: 'transparent',
										},
									}),
							}}
						>
							<Dot size={4} color={isSelected ? 'primary' : 'secondary'} />
						</ListItemIcon>
					)}
					<ListItemText
						primary={
							<Typography variant={'h6'} color={'inherit'}>
								{item.title}
							</Typography>
						}
					/>
					{(drawerOpen || level !== 1) && item.chip && (
						<Chip
							color={item.chip.color}
							variant={item.chip.variant}
							size={item.chip.size}
							label={item.chip.label}
							avatar={item.chip.avatar && <Avatar>{item.chip.avatar}</Avatar>}
						/>
					)}
				</ListItemButton>
			)}
		</>
	);
};

export default NavItem;
